import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { QrCode as QrIcon, X, Calendar, Filter, Search, RotateCcw, Car, Bike, Camera, User } from "lucide-react";
import ShareWhatsApp from "@/components/ShareWhatsApp";
import VisitorPhotoModal, { getVisitorPhotoSrc } from "@/components/VisitorPhotoModal";

function formatLogDateTime(isoStr) {
    if (!isoStr) return { date: "—", time: "" };
    try {
        const d = new Date(isoStr);
        if (isNaN(d.getTime())) return { date: "—", time: "" };
        const date = d.toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
        const time = d.toLocaleTimeString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
        return { date, time };
    } catch {
        return { date: "—", time: "" };
    }
}

function getLocalDateString(isoStr) {
    if (!isoStr) return "";
    try {
        const d = new Date(isoStr);
        if (isNaN(d.getTime())) return "";
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    } catch {
        return "";
    }
}

function VisitorRow({ v, isStaff, onEntry, onExit, onViewPhoto }) {
    const dateTime = formatLogDateTime(v.created_at || v.expected_at);
    const hasPhoto = Boolean(v._isWalkin || v.has_photo || v.photo_path || v.photo_url);
    const photoSrc = hasPhoto ? getVisitorPhotoSrc(v) : null;

    return (
        <div className="border-b border-nest-line hover:bg-nest-alt/25 transition" data-testid={`visitor-row-${v.code || v.walkin_id}`}>
            {/* Mobile Card View (< md) */}
            <div className="md:hidden p-3.5 space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                        {hasPhoto ? (
                            <button
                                type="button"
                                onClick={() => onViewPhoto(v)}
                                className="relative h-11 w-11 rounded-xl overflow-hidden border border-nest-line shrink-0 bg-nest-alt"
                                title="View photo"
                            >
                                <img src={photoSrc} alt="" className="h-full w-full object-cover" />
                            </button>
                        ) : (
                            <div className="h-11 w-11 rounded-xl border border-nest-line bg-nest-alt/70 shrink-0 grid place-items-center text-nest-ink3">
                                <User size={18} />
                            </div>
                        )}
                        <div className="min-w-0">
                            <p className="font-semibold text-sm text-nest-ink truncate">{v.name}</p>
                            <p className="text-xs text-nest-ink3 truncate">{v.phone || "—"}</p>
                        </div>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${
                        v.status === "entered" ? "bg-nest-sage text-white" :
                        v.status === "exited" ? "bg-nest-sand text-nest-ink" :
                        v.status === "approved" ? "bg-emerald-600 text-white" :
                        v.status === "rejected" ? "bg-nest-err text-white" :
                        "bg-nest-warn text-white"
                    }`}>{v.status}</span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs bg-nest-alt/40 p-2.5 rounded-xl border border-nest-line/60">
                    <div>
                        <span className="text-[10px] uppercase font-bold text-nest-ink3 block">Host / Unit</span>
                        <span className="font-semibold text-nest-ink truncate block">{v.host_flat || v.host_name || "—"}</span>
                    </div>
                    <div>
                        <span className="text-[10px] uppercase font-bold text-nest-ink3 block">Pass Code</span>
                        <span className="font-mono font-bold text-nest-ink block">{v._isWalkin ? "WALK-IN" : v.code}</span>
                    </div>
                    <div>
                        <span className="text-[10px] uppercase font-bold text-nest-ink3 block">Purpose</span>
                        <span className="text-nest-ink2 truncate block">{v.purpose || "Visitor"}</span>
                    </div>
                    <div>
                        <span className="text-[10px] uppercase font-bold text-nest-ink3 block">Date & Time</span>
                        <span className="text-nest-ink2 truncate block">{dateTime.date} {dateTime.time}</span>
                    </div>
                </div>

                {v.vehicle_no && (
                    <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 font-mono text-[10px] bg-nest-alt px-2 py-0.5 rounded border border-nest-line text-nest-ink">
                            {v.vehicle_type === "bike" ? <Bike size={11} className="text-nest-clay" /> : <Car size={11} className="text-nest-clay" />}
                            <span>{v.vehicle_no}</span>
                        </span>
                    </div>
                )}

                {isStaff && (v.status === "entered" || v.status === "approved" || (!v._isWalkin && v.status !== "exited")) && (
                    <div className="flex items-center gap-2 pt-1">
                        {(v.status === "approved" || (!v._isWalkin && v.status !== "entered" && v.status !== "exited")) && (
                            <button onClick={() => onEntry(v)} className="flex-1 text-xs py-2 rounded-lg bg-nest-ink text-white font-semibold text-center" data-testid={`entry-btn-${v.code || v.walkin_id}`}>Mark Entry</button>
                        )}
                        {isStaff && v.status === "entered" && (
                            <button onClick={() => onExit(v)} className="flex-1 text-xs py-2 rounded-lg border border-nest-line hover:bg-nest-alt font-semibold text-center" data-testid={`exit-btn-${v.code || v.walkin_id}`}>Mark Exit</button>
                        )}
                    </div>
                )}
            </div>

            {/* Desktop / Laptop Table View (>= md) */}
            <div className="hidden md:grid grid-cols-12 gap-3 p-4 items-center">
                <div className="col-span-3 flex items-center gap-3">
                    {hasPhoto ? (
                        <button
                            type="button"
                            onClick={() => onViewPhoto(v)}
                            className="relative group h-11 w-11 rounded-xl overflow-hidden border border-nest-line flex-shrink-0 bg-nest-alt hover:ring-2 hover:ring-nest-clay transition cursor-pointer"
                            title="Click to view visitor photo"
                            data-testid={`photo-btn-${v.code || v.walkin_id}`}
                        >
                            <img
                                src={photoSrc}
                                alt=""
                                className="h-full w-full object-cover group-hover:scale-110 transition duration-200"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = "none";
                                }}
                            />
                            <div className="absolute inset-0 bg-black/35 opacity-0 group-hover:opacity-100 flex items-center justify-center transition text-white">
                                <Camera size={13} />
                            </div>
                        </button>
                    ) : (
                        <div className="h-11 w-11 rounded-xl border border-nest-line bg-nest-alt/70 flex-shrink-0 grid place-items-center text-nest-ink3">
                            <User size={18} />
                        </div>
                    )}

                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-medium text-nest-ink truncate">{v.name}</p>
                            {v._isWalkin && (
                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-nest-clay/15 text-nest-clay">
                                    Walk-in
                                </span>
                            )}
                            {hasPhoto && (
                                <button
                                    type="button"
                                    onClick={() => onViewPhoto(v)}
                                    className="text-[10px] font-semibold text-nest-clay hover:underline inline-flex items-center gap-0.5"
                                    title="Check visitor photo"
                                    data-testid={`view-photo-link-${v.code || v.walkin_id}`}
                                >
                                    <Camera size={10} /> Photo
                                </button>
                            )}
                        </div>
                        <p className="text-xs text-nest-ink3 truncate">{v.phone || "—"}</p>
                    </div>
                </div>
                <div className="col-span-2 text-sm">
                    <p className="text-nest-ink2 truncate">{v.purpose}</p>
                    {v.vehicle_no && (
                        <span className="inline-flex items-center gap-1 font-mono text-[10px] bg-nest-alt px-1.5 py-0.5 rounded border border-nest-line text-nest-ink mt-0.5">
                            {v.vehicle_type === "bike" ? <Bike size={11} className="text-nest-clay" /> : <Car size={11} className="text-nest-clay" />}
                            <span>{v.vehicle_no}</span>
                        </span>
                    )}
                </div>
                <div className="col-span-2 text-sm">
                    <p className="font-mono text-xs text-nest-ink font-semibold">{v._isWalkin ? "WALK-IN" : v.code}</p>
                    <p className="text-xs text-nest-ink3 truncate">{v.host_flat || v.host_name}</p>
                </div>
                <div className="col-span-2 text-xs">
                    <p className="font-medium text-nest-ink flex items-center gap-1">
                        <Calendar size={12} className="text-nest-clay flex-shrink-0" />
                        <span>{dateTime.date}</span>
                    </p>
                    {dateTime.time && <p className="text-[11px] text-nest-ink3 mt-0.5 pl-4">{dateTime.time}</p>}
                </div>
                <div className="col-span-1">
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded inline-block ${
                        v.status === "entered" ? "bg-nest-sage text-white" :
                        v.status === "exited" ? "bg-nest-sand text-nest-ink" :
                        v.status === "approved" ? "bg-emerald-600 text-white" :
                        v.status === "rejected" ? "bg-nest-err text-white" :
                        "bg-nest-warn text-white"
                    }`}>{v.status}</span>
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                    {isStaff && (v.status === "approved" || (!v._isWalkin && v.status !== "entered" && v.status !== "exited")) && (
                        <button onClick={() => onEntry(v)} className="text-xs px-3 py-1.5 rounded-md bg-nest-ink text-white hover:bg-nest-ink2 transition" data-testid={`entry-btn-${v.code || v.walkin_id}`}>Mark entry</button>
                    )}
                    {isStaff && v.status === "entered" && (
                        <button onClick={() => onExit(v)} className="text-xs px-3 py-1.5 rounded-md border border-nest-line hover:bg-nest-alt transition" data-testid={`exit-btn-${v.code || v.walkin_id}`}>Mark exit</button>
                    )}
                </div>
            </div>
        </div>
    );
}

function NewVisitorModal({ open, onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", phone: "", purpose: "Family / Friend", vehicle_no: "", expected_at: "" });
    const [saving, setSaving] = useState(false);
    if (!open) return null;
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const r = await api.post("/visitors", form);
            toast.success("Visitor pass generated");
            onCreated(r.data);
            onClose();
        } catch {
            toast.error("Could not create visitor");
        } finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl border border-nest-line w-full max-w-lg p-8" onClick={(e) => e.stopPropagation()} data-testid="new-visitor-modal">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="nest-stat-label">New visitor</p>
                        <h3 className="font-display text-2xl mt-1 tracking-tight">Pre-approve a guest</h3>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md hover:bg-nest-alt"><X size={16}/></button>
                </div>
                <form onSubmit={submit} className="mt-6 grid gap-5">
                    <input required placeholder="Visitor name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="vname-input"/>
                    <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="vphone-input"/>
                    <select value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="vpurpose-input">
                        <option>Family / Friend</option>
                        <option>Delivery</option>
                        <option>Cab / Driver</option>
                        <option>Maid / Help</option>
                        <option>Other</option>
                    </select>
                    <input placeholder="Vehicle number (optional)" value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2 uppercase font-mono" data-testid="vvehicle-input"/>
                    <input type="datetime-local" value={form.expected_at} onChange={(e) => setForm({ ...form, expected_at: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="vexpected-input"/>
                    <button type="submit" disabled={saving} className="nest-btn-primary mt-2" data-testid="vsubmit-btn">{saving ? "Saving…" : "Generate pass"}</button>
                </form>
            </div>
        </div>
    );
}

function QRView({ visitor, onClose }) {
    if (!visitor) return null;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(visitor.code)}`;
    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl border border-nest-line w-full max-w-md p-8 text-center" onClick={(e) => e.stopPropagation()} data-testid="visitor-qr-modal">
                <p className="nest-stat-label">Share with your guest</p>
                <h3 className="font-display text-2xl mt-1 tracking-tight">{visitor.name}'s pass</h3>
                <img src={qrUrl} alt="QR" className="mx-auto mt-6 rounded-xl border border-nest-line" />
                <p className="font-mono text-2xl tracking-[0.3em] mt-6">{visitor.code}</p>
                <p className="text-xs text-nest-ink3 mt-2">Guard can scan QR or enter this code at gate.</p>
                <div className="mt-6 flex items-center justify-center gap-3">
                    <ShareWhatsApp
                        testid="qr-share-wa"
                        phone={visitor.phone}
                        label={visitor.phone ? "Send to guest" : "Share pass"}
                        text={`Hi ${visitor.name}, your entry pass for ${visitor.host_flat || "our society"} is *${visitor.code}*. Show this code (or QR) to the guard at the gate.\nQR: ${qrUrl}`}
                    />
                    <button onClick={onClose} className="nest-btn-primary" data-testid="qr-close-btn">Done</button>
                </div>
            </div>
        </div>
    );
}

export default function Visitors() {
    const { user } = useAuth();
    const [visitors, setVisitors] = useState([]);
    const [open, setOpen] = useState(false);
    const [qr, setQr] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [dateFilter, setDateFilter] = useState("all"); // "all" | "today" | "yesterday" | "week" | "custom"
    const [customDate, setCustomDate] = useState(""); // "YYYY-MM-DD"
    const [activePhotoVisitor, setActivePhotoVisitor] = useState(null);
    const isStaff = user?.role === "admin" || user?.role === "guard";

    const combineVisitors = (visitorList = [], walkinList = []) => {
        const normVisitors = visitorList.map((v) => ({ ...v, _isWalkin: false }));
        const normWalkins = walkinList.map((w) => ({
            ...w,
            _isWalkin: true,
            visitor_id: w.walkin_id,
            host_flat: `${w.tower}-${w.flat_no}`,
            code: (w.walkin_id || "").slice(-6).toUpperCase(),
        }));
        const combined = [...normVisitors, ...normWalkins];
        combined.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        return combined;
    };

    const load = async () => {
        try {
            const [vRes, wRes] = await Promise.all([
                api.get(isStaff ? "/visitors" : "/visitors/me").catch(() => ({ data: [] })),
                api.get("/walkins").catch(() => ({ data: [] })),
            ]);
            setVisitors(combineVisitors(vRes.data || [], wRes.data || []));
        } catch {}
    };
    useEffect(() => {
        load();
        const interval = setInterval(() => {
            if (typeof document !== "undefined" && document.visibilityState === "visible") {
                load();
            }
        }, 15000);
        return () => clearInterval(interval);
        /* eslint-disable-next-line */
    }, [user]);

    const onCreated = (v) => { setVisitors((arr) => [v, ...arr]); setQr(v); };
    const markEntry = async (v) => {
        try {
            const endpoint = v._isWalkin
                ? `/walkins/${v.walkin_id || v.visitor_id}/entry`
                : `/visitors/${v.visitor_id}/entry`;
            await api.post(endpoint);
            toast.success("Entry marked");
            await load();
        } catch { toast.error("Failed"); }
    };
    const markExit = async (v) => {
        try {
            const endpoint = v._isWalkin
                ? `/walkins/${v.walkin_id || v.visitor_id}/exit`
                : `/visitors/${v.visitor_id}/exit`;
            await api.post(endpoint);
            toast.success("Exit marked");
            await load();
        } catch { toast.error("Failed"); }
    };

    // Calculate reference dates
    const todayStr = getLocalDateString(new Date().toISOString());
    const yestDate = new Date();
    yestDate.setDate(yestDate.getDate() - 1);
    const yestStr = getLocalDateString(yestDate.toISOString());

    // Filter visitors
    const filteredVisitors = visitors.filter((v) => {
        // 1. Text filter
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            const matchesText = [
                v.name,
                v.phone,
                v.purpose,
                v.code,
                v.host_flat,
                v.host_name,
                v.vehicle_no,
            ].some((val) => (val || "").toString().toLowerCase().includes(q));
            if (!matchesText) return false;
        }

        // 2. Date filter
        const itemDateStr = getLocalDateString(v.created_at || v.expected_at);
        if (dateFilter === "today") {
            return itemDateStr === todayStr;
        }
        if (dateFilter === "yesterday") {
            return itemDateStr === yestStr;
        }
        if (dateFilter === "custom" && customDate) {
            return itemDateStr === customDate;
        }
        if (dateFilter === "week") {
            if (!v.created_at && !v.expected_at) return false;
            const itemD = new Date(v.created_at || v.expected_at);
            const now = new Date();
            const diffDays = (now - itemD) / (1000 * 60 * 60 * 24);
            return diffDays >= 0 && diffDays <= 7;
        }

        return true;
    });

    const clearAllFilters = () => {
        setSearchQuery("");
        setDateFilter("all");
        setCustomDate("");
    };

    return (
        <div className="animate-fade-up" data-testid="visitors-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 sm:gap-4">
                <div>
                    <p className="nest-stat-label">Visitors</p>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-1 sm:mt-2">{isStaff ? "Gate ledger" : "Your guest list"}</h1>
                    <p className="text-nest-ink2 text-sm sm:text-base mt-1 sm:mt-2 max-w-xl">{isStaff ? "Every pre-approved entry, in chronological order." : "Pre-approve a guest. We'll generate a QR pass to share."}</p>
                </div>
                {user?.role === "resident" && (
                    <button onClick={() => setOpen(true)} className="nest-btn-primary flex items-center justify-center gap-2 text-sm py-3 w-full sm:w-auto shadow-xs" data-testid="new-visitor-btn"><QrIcon size={16}/> New visitor pass</button>
                )}
            </div>

            {/* Filter toolbar */}
            <div className="mt-6 sm:mt-8 bg-white p-3.5 sm:p-4 rounded-2xl border border-nest-line shadow-xs space-y-3">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    {/* Search */}
                    <div className="relative flex-1 w-full md:max-w-md">
                        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-nest-ink3" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by guest, flat, code, or vehicle..."
                            className="w-full pl-10 pr-8 py-2.5 bg-nest-alt/50 rounded-xl border border-nest-line focus:border-nest-clay focus:ring-1 focus:ring-nest-clay outline-none text-xs text-nest-ink placeholder:text-nest-ink3 transition"
                            data-testid="visitor-search-input"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery("")}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-nest-ink3 hover:text-nest-ink p-1"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    {/* Date picker */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-nest-line bg-nest-alt/50 focus-within:border-nest-clay transition">
                            <Calendar size={14} className="text-nest-clay shrink-0" />
                            <span className="text-[11px] font-bold uppercase tracking-wider text-nest-ink2">Date:</span>
                            <input
                                type="date"
                                value={customDate}
                                onChange={(e) => {
                                    setCustomDate(e.target.value);
                                    setDateFilter(e.target.value ? "custom" : "all");
                                }}
                                className="text-xs bg-transparent outline-none font-sans text-nest-ink cursor-pointer"
                                data-testid="visitor-date-picker"
                            />
                            {customDate && (
                                <button
                                    onClick={() => { setCustomDate(""); setDateFilter("all"); }}
                                    className="text-nest-ink3 hover:text-nest-ink p-0.5"
                                    title="Clear date"
                                >
                                    <X size={12} />
                                </button>
                            )}
                        </div>

                        {(dateFilter !== "all" || searchQuery) && (
                            <button
                                onClick={clearAllFilters}
                                className="inline-flex items-center gap-1 text-xs text-nest-clay hover:text-nest-clayDark font-medium px-2 py-1 rounded-lg hover:bg-nest-alt transition"
                            >
                                <RotateCcw size={12} /> Clear filters
                            </button>
                        )}
                    </div>
                </div>

                {/* Quick Filter Pills */}
                <div className="flex items-center gap-1.5 pt-2 border-t border-nest-line/60 overflow-x-auto no-scrollbar pb-0.5">
                    <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-nest-ink3 mr-1 flex items-center gap-1 shrink-0">
                        <Filter size={11} /> Filter:
                    </span>
                    {[
                        { id: "all", label: "All Logs" },
                        { id: "today", label: "Today" },
                        { id: "yesterday", label: "Yesterday" },
                        { id: "week", label: "Past 7 Days" },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => {
                                setDateFilter(tab.id);
                                if (tab.id !== "custom") setCustomDate("");
                            }}
                            className={`px-3 py-1 rounded-lg text-xs font-medium transition whitespace-nowrap shrink-0 ${
                                dateFilter === tab.id
                                    ? "bg-nest-clay text-white shadow-2xs font-semibold"
                                    : "bg-nest-alt text-nest-ink2 hover:bg-nest-sand/40 hover:text-nest-ink border border-nest-line"
                            }`}
                            data-testid={`filter-${tab.id}`}
                        >
                            {tab.label}
                        </button>
                    ))}

                    <div className="ml-auto text-xs text-nest-ink3">
                        Showing <span className="font-semibold text-nest-ink">{filteredVisitors.length}</span> of {visitors.length} entries
                    </div>
                </div>
            </div>

            {/* Ledger table */}
            <div className="nest-card mt-6 overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-3 p-4 bg-nest-alt nest-stat-label border-b border-nest-line">
                    <div className="col-span-3">Guest</div>
                    <div className="col-span-2">Purpose / Vehicle</div>
                    <div className="col-span-2">Code / Host</div>
                    <div className="col-span-2">Date & Time</div>
                    <div className="col-span-1">Status</div>
                    <div className="col-span-2 text-right">Actions</div>
                </div>
                {filteredVisitors.length === 0 && (
                    <div className="p-10 text-center text-nest-ink2" data-testid="no-visitors">
                        <p className="font-medium text-sm">No visitor logs match your filters.</p>
                        {(dateFilter !== "all" || searchQuery) && (
                            <button
                                onClick={clearAllFilters}
                                className="mt-2 text-xs text-nest-clay hover:underline inline-flex items-center gap-1"
                            >
                                <RotateCcw size={12} /> Clear filters to show all logs
                            </button>
                        )}
                    </div>
                )}
                {filteredVisitors.map((v) => (
                    <VisitorRow
                        key={v.visitor_id || v.walkin_id}
                        v={v}
                        isStaff={isStaff}
                        onEntry={markEntry}
                        onExit={markExit}
                        onViewPhoto={setActivePhotoVisitor}
                    />
                ))}
            </div>

            <NewVisitorModal open={open} onClose={() => setOpen(false)} onCreated={onCreated} />
            <QRView visitor={qr} onClose={() => setQr(null)} />
            <VisitorPhotoModal visitor={activePhotoVisitor} onClose={() => setActivePhotoVisitor(null)} />
        </div>
    );
}

